import { useCallback } from "react";
import { z } from "zod";
import { Button } from "@zapier/design-system";
import { useCopilotChat } from "@zapier/ai-toolkit/client";
import { currentAccountIdAtom } from "@zapier/canvas-state";
import { useAtomValue } from "jotai";

import { useCreateAppConnection } from "./useCreateAppConnection";
import styles from "./buildModeSuggestionsClient.module.css";
import { BuildModeSuggestionsParamsSchema } from "./index";
import { BuildModeSuggestionType } from "./types";

export type BuildModeSuggestionsParams = z.infer<
  typeof BuildModeSuggestionsParamsSchema
>;

const promptMapping = {
  [BuildModeSuggestionType.SETUP_ZAPS]:
    "Help me configure field mapping for the Zaps on this canvas. Please work through them one at a time and ask for any additional information you need.",
  [BuildModeSuggestionType.LEAD_GENERATION]:
    "Build a lead generation system for me.",
  [BuildModeSuggestionType.CUSTOMER_SUPPORT]:
    "Build a customer support automation for me.",
  [BuildModeSuggestionType.ECOMMERCE_ORDER]:
    "Build an e-commerce order processing system for me.",
  [BuildModeSuggestionType.TOUR_MY_CANVAS]: "Use the tour tool",
  [BuildModeSuggestionType.EXPLAIN_MY_CANVAS]:
    "Explain what I have built on this canvas and how it works.",
};

export function BuildModeSuggestions(props: {
  args: BuildModeSuggestionsParams;
}) {
  const { setMessages, reload } = useCopilotChat();
  const accountId = useAtomValue(currentAccountIdAtom);
  const { createNewConnection } = useCreateAppConnection(Number(accountId));
  const args = props?.args || {};
  const { suggestions, selected_api } = args;

  const handleSuggestionClick = useCallback(
    async (suggestion: BuildModeSuggestionType) => {
      const promptText =
        promptMapping[suggestion as keyof typeof promptMapping] || suggestion;

      // Use setMessages to append user message to conversation
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          role: "user",
          content: promptText,
          id: `user-${Date.now()}`,
          createdAt: new Date(),
          parts: [{ type: "text", text: promptText }],
        },
      ]);

      // Trigger LLM response after adding the user message
      await reload();
    },
    [setMessages, reload],
  );

  const connectApp = useCallback(async () => {
    if (!selected_api) {
      return;
    }
    const res = await fetch(
      `/api/v4/implementations-meta/public?selected_apis=${selected_api}`,
    );
    const data = await res.json();
    const { id } = await createNewConnection(data.results[0]);
    return id;
  }, [selected_api, createNewConnection]);

  const handleButtonClick = useCallback(
    async (suggestion: BuildModeSuggestionType) => {
      // Check if this is an auth creation action
      if (suggestion === BuildModeSuggestionType.CREATE_AUTH) {
        if (!selected_api) {
          // Show error message if CREATE_AUTH is suggested without selected_api
          const errorMessage =
            "Cannot create auth connection: No API selected. The LLM must provide selected_api when suggesting CREATE_AUTH.";
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              role: "assistant",
              content: errorMessage,
              id: `assistant-error-${Date.now()}`,
              createdAt: new Date(),
              parts: [{ type: "text", text: errorMessage }],
            },
          ]);
          return;
        }

        const id = await connectApp();

        // Push a message to chat about the auth creation
        let authMessage: string = "";
        if (id) {
          authMessage = `Auth created successfully with ID: ${id}`;
        } else {
          authMessage = `Failed to create connection to ${selected_api}`;
        }

        setMessages((prevMessages) => [
          ...prevMessages,
          {
            role: "user",
            content: authMessage,
            id: `user-${Date.now()}`,
            createdAt: new Date(),
            parts: [{ type: "text", text: authMessage }],
          },
        ]);
        await reload();
      } else {
        // Otherwise, treat it as a chat suggestion
        await handleSuggestionClick(suggestion);
      }
    },
    [connectApp, handleSuggestionClick, selected_api, setMessages, reload],
  );

  return (
    <div className={styles.container}>
      {suggestions?.map((suggestion) => (
        <Button
          key={suggestion}
          variant="primary"
          size="compact"
          onPress={() => handleButtonClick(suggestion)}
          className={styles.suggestionButton}
        >
          {suggestion}
        </Button>
      ))}
    </div>
  );
}
